import React, { useMemo, useState, useEffect } from 'react';
import {
  Users, Search as SearchIcon, Linkedin,
  Sparkles, Mail, QrCode, ArrowRightLeft, GitMerge,
  Home, Briefcase, AlertTriangle,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import EntityLink from '../components/shared/EntityLink';
import { EditableCell } from '../components/EditableCell';
import LinkedinUrlImport from '../components/contacts/LinkedinUrlImport';
import ApolloEnrichContactModal from '../components/ApolloEnrichContactModal';
import TransferContactModal from '../components/TransferContactModal';
import MergeContactsModal from '../components/MergeContactsModal';
import ContactVCardQR from '../components/ContactVCardQR';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabaseClient';
import { formatPersonName } from '../utils/nameUtils';

// Cores por vertical (valor de v_contato_hub.vertical) — mantém a convenção visual do
// app, com fallback neutro para labels novos. O filtro Vertical é dinâmico (não hardcoda).
const VERTICAL_STYLE = {
  Lands: 'bg-blue-50 text-blue-700',
  Launches: 'bg-violet-50 text-violet-700',
};
const BRL = (v) => (v == null ? '' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }));

export default function ContatosPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [decisorOnly, setDecisorOnly] = useState(false);
  const [comEmailOnly, setComEmailOnly] = useState(false);
  const [tipoFilter, setTipoFilter] = useState('');
  // Fase 1B-1: papel vira multi-toggle combinável (AND), substituindo o dropdown único.
  const [propOnly, setPropOnly] = useState(false);
  const [socioOnly, setSocioOnly] = useState(false);
  const [negociadorOnly, setNegociadorOnly] = useState(false);
  const [pendenciasOnly, setPendenciasOnly] = useState(false);
  const [semComDias, setSemComDias] = useState(''); // '' | '30' | '60' | '90' — sem comunicação há > N dias
  const [verticalFilter, setVerticalFilter] = useState(''); // '' = todas; '__none__' = sem vertical; senão = label de v_contato_hub.vertical
  const [focusContatoId, setFocusContatoId] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [enrichContact, setEnrichContact] = useState(null);
  const [transferContact, setTransferContact] = useState(null);
  const [mergeContact, setMergeContact] = useState(null);
  const [qrContact, setQrContact] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkEnriching, setBulkEnriching] = useState(false);

  // Synthesize an empresa object from the row's empresa_id/empresa_nome — enough
  // for display + Transfer/Merge/QR/Enrich. Real id enables ApolloEnrich Strategy 2 + churn.
  const buildEmpresa = (row) => (row?.empresa_id
    ? { id: row.empresa_id, company_name: row.empresa_nome, empresa: row.empresa_nome }
    : null);

  // v_contato_hub omits apollo_person_id/apollo_linkedin_url, so Strategy 1
  // (direct enrich) would die without an extra fetch. Pull them on click.
  const handleEnrichClick = async (row) => {
    const { data: extras } = await supabase
      .from('contatos')
      .select('apollo_person_id, apollo_linkedin_url')
      .eq('id', row.id)
      .single();
    setEnrichContact({ ...row, title: row.cargo, ...(extras || {}) });
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkEnrich = async () => {
    if (selectedIds.size > 25) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Você selecionou ${selectedIds.size} contatos. Isso consumirá até ${selectedIds.size} créditos Apollo. Continuar?`)) return;
    }
    setBulkEnriching(true);
    let success = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        const { error } = await supabase.functions.invoke('enrich-companies-via-apollo', {
          body: { contato_id: id, level: 'basic', decided_by: user?.email || 'ui_bulk_enrich' },
        });
        if (error) failed += 1;
        else success += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkEnriching(false);
    // eslint-disable-next-line no-alert
    alert(`Enriquecimento concluído: ${success} sucesso, ${failed} falha(s)`);
    setSelectedIds(new Set());
    refetch();
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const location = useLocation();

  // Fase 1B-1 — FONTE ÚNICA: a listagem lê de v_contato_hub (D-08, sem registro paralelo).
  // A view já expõe vertical, papéis (flags), counts e ultima_comunicacao_at prontos,
  // e já filtra merged_into_id IS NULL (duplicatas arquivadas não aparecem).
  const { data, loading, error, refetch } = useSupabaseQuery(
    (sb) => sb.from('v_contato_hub').select('*').order('nome_completo'),
    []
  );

  // Opções do filtro Vertical derivadas dos labels presentes (vocabulário emergente
  // da view: "Formação de Áreas", "Empreendimentos", …) — robusto a novos valores.
  const verticalOptions = useMemo(
    () => Array.from(new Set((data || []).map((r) => r.vertical).filter(Boolean))).sort(),
    [data],
  );

  // Onda 5 abr/26: deep-link de /proprietarios (PF) — focar contato + preencher busca + scroll + highlight temp.
  useEffect(() => {
    const id = location.state?.focusContatoId;
    if (!id || !data) return;
    const contato = data.find((c) => c.id === id);
    if (contato) {
      setSearch([contato.first_name, contato.last_name].filter(Boolean).join(' '));
      setFocusContatoId(id);
      setTimeout(() => {
        const el = document.getElementById(`contato-row-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      const fade = setTimeout(() => setFocusContatoId(null), 3000);
      window.history.replaceState({}, '');
      return () => clearTimeout(fade);
    }
    window.history.replaceState({}, '');
  }, [location.state, data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const semComCutoff = semComDias ? Date.now() - Number(semComDias) * 86400000 : null;
    return data.filter((c) => {
      if (search) {
        const q = search.toLowerCase();
        const hit =
          (c.nome_completo || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.empresa_nome || '').toLowerCase().includes(q) ||
          (c.cargo || '').toLowerCase().includes(q) ||
          (c.phone || '').toLowerCase().includes(q) ||
          (c.mobile || '').toLowerCase().includes(q) ||
          (c.person_linkedin_url || '').toLowerCase().includes(q) ||
          (c.cpf || '').includes(q)
          || (c.imoveis_busca || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (decisorOnly && !c.eh_decisor) return false;
      if (comEmailOnly && !c.email) return false;
      if (tipoFilter && c.tipo_contato !== tipoFilter) return false;
      // Papéis multi-toggle (AND): cada flag marcada deve ser verdadeira.
      if (propOnly && !c.is_proprietario) return false;
      if (socioOnly && !c.is_socio) return false;
      if (negociadorOnly && !c.is_negociador) return false;
      if (pendenciasOnly && !(c.pendencias_count > 0)) return false;
      // Vertical agora é o label de v_contato_hub.vertical ('__none__' = sem vertical).
      if (verticalFilter === '__none__' && c.vertical) return false;
      if (verticalFilter && verticalFilter !== '__none__' && c.vertical !== verticalFilter) return false;
      // "Sem comunicação há > N dias": nunca comunicou (null) OU última < cutoff.
      if (semComCutoff !== null) {
        const last = c.ultima_comunicacao_at ? new Date(c.ultima_comunicacao_at).getTime() : null;
        if (last !== null && last >= semComCutoff) return false;
      }
      return true;
    });
  }, [data, search, decisorOnly, comEmailOnly, tipoFilter, propOnly, socioOnly, negociadorOnly, pendenciasOnly, semComDias, verticalFilter]);

  const sorted = useMemo(() => {
    if (!sortField || !filtered) return filtered;
    return [...filtered].sort((a, b) => {
      let va; let vb;
      switch (sortField) {
        case 'nome': va = a.nome_completo || ''; vb = b.nome_completo || ''; break;
        case 'empresa': va = a.empresa_nome || ''; vb = b.empresa_nome || ''; break;
        case 'cargo': va = a.cargo || ''; vb = b.cargo || ''; break;
        case 'email': va = a.email || ''; vb = b.email || ''; break;
        case 'celular': va = a.mobile || ''; vb = b.mobile || ''; break;
        case 'emails_count': va = a.emails_count || 0; vb = b.emails_count || 0; break;
        case 'ultima_comunicacao': va = a.ultima_comunicacao_at || ''; vb = b.ultima_comunicacao_at || ''; break;
        default: return 0;
      }
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [filtered, sortField, sortDir]);

  const SortHeader = ({ field, label, align = 'text-left', width = 80 }) => (
    <th
      className={`px-3 py-2 ${align} cursor-pointer hover:text-blue-700 select-none overflow-hidden resize-x`}
      style={{ width: `${width}px` }}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDir === 'asc' ? '▲' : '▼')}
    </th>
  );

  const updateContato = async (id, patch) => {
    await supabase
      .from('contatos')
      .update({
        ...patch,
        updated_at: new Date().toISOString(),
        updated_by: user?.email || user?.id || null,
      })
      .eq('id', id);
    refetch();
  };

  const toggleDecisor = async (id, checked) => {
    await supabase
      .from('contatos')
      .update({
        eh_decisor: checked,
        decisor_source: checked ? 'human' : null,
        decisor_confidence: checked ? 1.0 : null,
        updated_at: new Date().toISOString(),
        updated_by: user?.email || user?.id || null,
      })
      .eq('id', id);
    refetch();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader title="Contatos" subtitle="Listagem global" icon={Users} />
      <main className="w-full px-4 lg:px-6 2xl:px-8 py-4 lg:py-6 space-y-3">
        {/* Sprint 1 E1.4b — Importar contato via URL LinkedIn (Apollo) */}
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <LinkedinUrlImport onImported={() => refetch()} />
        </div>
        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nome, email, empresa, cargo, telefone, LinkedIn, CPF, imovel (contribuinte/endereco/matricula)..."
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
            />
          </div>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={decisorOnly} onChange={(e) => setDecisorOnly(e.target.checked)} />
            Só decisores
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={comEmailOnly} onChange={(e) => setComEmailOnly(e.target.checked)} />
            Só com email
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={propOnly} onChange={(e) => setPropOnly(e.target.checked)} />
            É proprietário
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={socioOnly} onChange={(e) => setSocioOnly(e.target.checked)} />
            É sócio
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={negociadorOnly} onChange={(e) => setNegociadorOnly(e.target.checked)} />
            É negociador
          </label>
          <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={pendenciasOnly} onChange={(e) => setPendenciasOnly(e.target.checked)} />
            Tem pendências
          </label>
          <select
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-blue-400"
          >
            <option value="">Tipo: Todos</option>
            <option value="incorporadora">Incorporadora</option>
            <option value="socio">Sócio</option>
            <option value="proprietario">Proprietário</option>
            <option value="consultor">Consultor</option>
          </select>
          <select
            value={verticalFilter}
            onChange={(e) => setVerticalFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-blue-400"
          >
            <option value="">Vertical: Todas</option>
            {verticalOptions.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
            <option value="__none__">Sem vertical</option>
          </select>
          <select
            value={semComDias}
            onChange={(e) => setSemComDias(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-blue-400"
            title="Contatos sem comunicação (email/WhatsApp/chamada) há mais de N dias"
          >
            <option value="">Comunicação: Todas</option>
            <option value="30">Sem contato &gt; 30d</option>
            <option value="60">Sem contato &gt; 60d</option>
            <option value="90">Sem contato &gt; 90d</option>
          </select>
          <div className="text-[11px] text-slate-500 ml-auto">
            {loading ? '...' : `${filtered.length}${data ? ` / ${data.length}` : ''}`}
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center justify-between">
            <span className="text-xs text-blue-800 font-medium">
              {selectedIds.size} contato(s) selecionado(s)
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleBulkEnrich}
                disabled={bulkEnriching}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
              >
                <Sparkles size={12} />
                {bulkEnriching ? 'Enriquecendo...' : 'Enriquecer selecionados'}
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkEnriching}
                className="px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50"
              >
                Limpar seleção
              </button>
            </div>
          </div>
        )}

        {loading && !data && <LoadingSpinner label="Carregando contatos..." />}
        {error && !data && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && data && filtered.length === 0 && (
          <EmptyState
            message={search ? 'Nenhum contato bate com a busca' : 'Nenhum contato cadastrado'}
            hint={search ? 'Tente relaxar os filtros.' : 'Contatos são criados via enriquecimento Apollo ou manualmente na ficha da empresa.'}
            icon={Users}
          />
        )}

        {enrichContact && (
          <ApolloEnrichContactModal
            contato={enrichContact}
            empresa={buildEmpresa(enrichContact)}
            userEmail={user?.email}
            onClose={() => setEnrichContact(null)}
            onApplied={() => { setEnrichContact(null); refetch(); }}
          />
        )}
        {transferContact && (
          <TransferContactModal
            contact={transferContact}
            currentEmpresa={buildEmpresa(transferContact)}
            onClose={() => setTransferContact(null)}
            onTransferred={() => { setTransferContact(null); refetch(); }}
          />
        )}
        {mergeContact && (
          <MergeContactsModal
            masterContact={{ ...mergeContact, empresas: buildEmpresa(mergeContact) }}
            duplicateContact={null}
            onClose={() => setMergeContact(null)}
            onMerged={() => { setMergeContact(null); refetch(); }}
          />
        )}
        {qrContact && (
          <ContactVCardQR
            contact={qrContact}
            company={buildEmpresa(qrContact)}
            onClose={() => setQrContact(null)}
          />
        )}

        {data && filtered.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg">
            <div>
              <table className="w-full text-xs min-w-[2200px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-center" style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={sorted.length > 0 && sorted.every((r) => selectedIds.has(r.id))}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (checked) sorted.forEach((r) => next.add(r.id));
                            else sorted.forEach((r) => next.delete(r.id));
                            return next;
                          });
                        }}
                        title="Selecionar todos os contatos visíveis"
                      />
                    </th>
                    <SortHeader field="nome" label="Nome" width={220} />
                    <SortHeader field="empresa" label="Empresa" width={200} />
                    <th className="px-3 py-2 text-left overflow-hidden resize-x" style={{ width: '140px' }}>Vertical</th>
                    <SortHeader field="cargo" label="Cargo" width={180} />
                    <SortHeader field="email" label="Email" width={240} />
                    <SortHeader field="celular" label="Celular" width={170} />
                    <th className="px-3 py-2 text-left overflow-hidden resize-x" style={{ width: '180px' }}>Telefone</th>
                    <th className="px-3 py-2 text-center overflow-hidden resize-x" style={{ width: '70px' }}>Decisor</th>
                    <th className="px-3 py-2 text-left overflow-hidden resize-x" style={{ width: '150px' }}>Papéis</th>
                    <th className="px-3 py-2 text-left overflow-hidden resize-x" style={{ width: '200px' }}>Relações</th>
                    <SortHeader field="emails_count" label="Emails" align="text-right" width={70} />
                    <SortHeader field="ultima_comunicacao" label="Última comunicação" width={140} />
                    <th className="px-3 py-2 text-center overflow-hidden resize-x" style={{ width: '60px' }}>Apollo</th>
                    <th className="px-3 py-2 text-center overflow-hidden resize-x" style={{ width: '160px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => {
                    const isFocused = focusContatoId === row.id;
                    return (
                      <tr
                        key={row.id}
                        id={`contato-row-${row.id}`}
                        className={`border-t border-slate-100 hover:bg-slate-50 scroll-mt-[180px] ${isFocused ? 'ring-2 ring-blue-400 bg-blue-50/40' : ''}`}
                      >
                        {/* Bulk-select checkbox */}
                        <td className="px-3 py-1.5 text-center align-top" style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            className="rounded border-slate-300"
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleSelectOne(row.id)}
                            aria-label={`Selecionar ${row.nome_completo || row.id}`}
                          />
                        </td>

                        {/* Nome: EntityLink (abre modal) + edição inline first/last embaixo */}
                        <td className="px-3 py-1.5 align-top min-w-[220px] max-w-[220px]" title={row.nome_completo || ''}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <EntityLink
                              entityType="contato"
                              entityId={row.id}
                              label={row.nome_completo || '—'}
                              showIcon={false}
                            />
                            {row.person_linkedin_url && (
                              <a
                                href={row.person_linkedin_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-300 hover:text-blue-700 flex-shrink-0"
                                title="LinkedIn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Linkedin size={11} />
                              </a>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-1 text-[10px] text-slate-500">
                            <div className="min-w-0">
                              <EditableCell
                                value={row.first_name || ''}
                                placeholder="nome"
                                onSave={(v) => updateContato(row.id, { first_name: formatPersonName(v) || null })}
                              />
                            </div>
                            <div className="min-w-0">
                              <EditableCell
                                value={row.last_name || ''}
                                placeholder="sobrenome"
                                onSave={(v) => updateContato(row.id, { last_name: formatPersonName(v) || null })}
                              />
                            </div>
                          </div>
                        </td>

                        {/* Empresa: EntityLink (não editar inline) */}
                        <td className="px-3 py-1.5 align-top min-w-[200px] max-w-[200px]" title={row.empresa_nome || ''}>
                          {row.empresa_id ? (
                            <EntityLink
                              entityType="empresa"
                              entityId={row.empresa_id}
                              label={row.empresa_nome || '—'}
                              showIcon={false}
                            />
                          ) : (
                            <span className="text-slate-400 italic text-[11px]">— sem empresa —</span>
                          )}
                        </td>

                        {/* Vertical — direto de v_contato_hub.vertical (label da business unit) */}
                        <td className="px-3 py-1.5 align-top min-w-[140px] max-w-[140px]">
                          {row.vertical ? (
                            <span
                              className={`inline-flex items-center gap-1 rounded text-[10px] px-1.5 py-0.5 font-medium ${VERTICAL_STYLE[row.vertical] || 'bg-slate-100 text-slate-600'}`}
                              title={`Vertical: ${row.vertical}`}
                            >
                              {row.vertical}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-400" title="Sem vínculo de vertical">
                              Sem vertical
                            </span>
                          )}
                        </td>

                        {/* Cargo (DB: title) */}
                        <td className="px-3 py-1.5 align-top min-w-[180px] max-w-[180px]" title={row.cargo || ''}>
                          <EditableCell
                            value={row.cargo || ''}
                            onSave={(v) => updateContato(row.id, { title: v || null })}
                            placeholder="Cargo"
                          />
                        </td>

                        {/* Email */}
                        <td className="px-3 py-1.5 align-top min-w-[240px] max-w-[240px]" title={row.email || ''}>
                          <EditableCell
                            type="email"
                            value={row.email || ''}
                            onSave={(v) => updateContato(row.id, { email: v || null })}
                            placeholder="email@..."
                          />
                        </td>

                        {/* Celular (DB: mobile) */}
                        <td className="px-3 py-1.5 align-top min-w-[170px] max-w-[170px]" title={row.mobile || ''}>
                          <EditableCell
                            type="tel"
                            value={row.mobile || ''}
                            onSave={(v) => updateContato(row.id, { mobile: v || null })}
                            placeholder="+55 11 9..."
                          />
                        </td>

                        {/* Telefone (DB: phone) — fixo com possível ramal */}
                        <td className="px-3 py-1.5 align-top min-w-[180px] max-w-[180px]" title={row.phone || ''}>
                          <EditableCell
                            type="tel"
                            value={row.phone || ''}
                            onSave={(v) => updateContato(row.id, { phone: v || null })}
                            placeholder="+55 11 3..."
                          />
                        </td>

                        {/* Decisor toggle */}
                        <td className="px-3 py-1.5 text-center min-w-[70px] max-w-[70px]">
                          <input
                            type="checkbox"
                            checked={!!row.eh_decisor}
                            className="rounded border-slate-300"
                            title={row.decisor_source ? `Fonte: ${row.decisor_source} (${row.decisor_confidence ?? '?'})` : 'marcar como decisor'}
                            onChange={(e) => toggleDecisor(row.id, e.target.checked)}
                          />
                        </td>

                        {/* Papéis — badges das flags de v_contato_hub (inclui Decisor) */}
                        <td className="px-3 py-1.5 align-top min-w-[150px] max-w-[150px]">
                          <div className="flex flex-wrap gap-0.5">
                            {row.is_proprietario && (
                              <span className="px-1.5 py-0.5 text-[9px] bg-green-100 text-green-700 rounded" title="Proprietário (PF)">Proprietário</span>
                            )}
                            {row.is_socio && (
                              <span className="px-1.5 py-0.5 text-[9px] bg-indigo-100 text-indigo-700 rounded" title="Sócio de empresa">Sócio</span>
                            )}
                            {row.is_negociador && (
                              <span className="px-1.5 py-0.5 text-[9px] bg-blue-100 text-blue-700 rounded" title="Negociador ativo">Negociador</span>
                            )}
                            {row.eh_decisor && (
                              <span className="px-1.5 py-0.5 text-[9px] bg-amber-100 text-amber-700 rounded" title="Decisor">Decisor</span>
                            )}
                            {!row.is_proprietario && !row.is_socio && !row.is_negociador && !row.eh_decisor && (
                              <span className="text-slate-300 text-[10px]">—</span>
                            )}
                          </div>
                        </td>

                        {/* Relações — counts de v_contato_hub (imóveis, negócios, pendências) */}
                        <td className="px-3 py-1.5 align-top min-w-[200px] max-w-[200px]">
                          <div className="flex flex-wrap items-center gap-1">
                            {row.imoveis_proprietario_count > 0 && (<span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-green-50 text-green-700 rounded" title={row.imoveis_resumo || ''}><Home size={9} />{row.imoveis_proprietario_count}</span>)}
                            {row.ofertas_negociador_count > 0 && (<span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-blue-50 text-blue-700 rounded" title={`Estagios: ${row.ofertas_estagios || '—'} · ${BRL(row.ofertas_valor_proposto_total) || 'sem valor'}`}><Briefcase size={9} />{row.ofertas_negociador_count}</span>)}
                            {row.pendencias_count > 0 && (<span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-red-100 text-red-700 font-semibold rounded" title={`${row.pendencias_count} pendencia(s)`}><AlertTriangle size={9} />{row.pendencias_count}</span>)}
                            {!(row.imoveis_proprietario_count > 0) && !(row.ofertas_negociador_count > 0) && !(row.pendencias_count > 0) && (<span className="text-slate-300 text-[10px]">—</span>)}
                          </div>
                          {row.imoveis_resumo && (<div className="text-[10px] text-slate-500 truncate mt-0.5" title={row.imoveis_resumo}>🏠 {row.imoveis_resumo}</div>)}
                          {row.ofertas_negociador_count > 0 && (<div className="text-[10px] text-slate-500 truncate" title={`Estagios: ${row.ofertas_estagios || '—'}`}>💼 {row.ofertas_negociador_count} tratativa(s){row.ofertas_valor_proposto_total ? ` · ${BRL(row.ofertas_valor_proposto_total)}` : ''}</div>)}
                        </td>

                        {/* Emails count */}
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-700 min-w-[70px] max-w-[70px]">
                          {row.emails_count > 0 ? row.emails_count : <span className="text-slate-300">—</span>}
                        </td>

                        {/* Última comunicação — consolida email + WhatsApp + chamada (v_contato_hub) */}
                        <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap text-[11px] min-w-[140px] max-w-[140px]">
                          {row.ultima_comunicacao_at
                            ? new Date(row.ultima_comunicacao_at).toLocaleDateString('pt-BR')
                            : <span className="text-slate-400">—</span>}
                        </td>

                        {/* Apollo */}
                        <td className="px-3 py-1.5 text-center min-w-[60px] max-w-[60px]">
                          {row.has_apollo
                            ? <span className="text-emerald-600" title="Enriquecido via Apollo">✓</span>
                            : <span className="text-slate-300">—</span>}
                        </td>

                        {/* Ações */}
                        <td className="px-3 py-1.5 min-w-[160px] max-w-[160px]">
                          <div className="flex items-center justify-end gap-0.5">
                            {row.person_linkedin_url && (
                              <a
                                href={row.person_linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-slate-400 hover:text-blue-600 rounded"
                                title="Abrir LinkedIn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Linkedin size={12} />
                              </a>
                            )}
                            <button
                              onClick={() => handleEnrichClick(row)}
                              className="p-1 text-slate-400 hover:text-amber-600 rounded"
                              title="Enriquecer via Apollo"
                            >
                              <Sparkles size={12} />
                            </button>
                            {row.email && (
                              <a
                                href={`mailto:${row.email}`}
                                className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                                title="Enviar email"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Mail size={12} />
                              </a>
                            )}
                            <button
                              onClick={() => setQrContact({ ...row, title: row.cargo })}
                              className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                              title="QR vCard"
                            >
                              <QrCode size={12} />
                            </button>
                            {row.empresa_id && (
                              <button
                                onClick={() => setTransferContact({ ...row, title: row.cargo })}
                                className="p-1 text-slate-400 hover:text-orange-600 rounded"
                                title="Transferir para outra empresa"
                              >
                                <ArrowRightLeft size={12} />
                              </button>
                            )}
                            <button
                              onClick={() => setMergeContact({ ...row, title: row.cargo })}
                              className="p-1 text-slate-400 hover:text-violet-600 rounded"
                              title="Merge com outro contato"
                            >
                              <GitMerge size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
